import fire from '@store/firebase';
import { deleteImgStorage, updateImgAndGetURL } from '@store/firebase/storage';
import { CollectionPath } from '@typings/collections.enum';
import { User } from '@typings/setting/userType.interface';
import { AUTH_TYPE } from '@typings/common/authorizationType';
import {
  addWalletHistoryByType,
  WalletHistoryType,
} from './addWalletHistoryByType';

const firestore = fire.firestore();
const usersRef = firestore.collection(CollectionPath.USERS);
const auth = fire.auth();
const user = auth.currentUser;

export async function setUserDisplayName(displayName: string) {
  if (user) {
    await user.updateProfile({
      displayName: displayName,
    });
  }
}

type Props = {
  data?: User | any;
  userUID: string;
  representativeChar?: {
    representativePhotocardId?: string;
    representativePhotocardPath?: string;
  };
  fileList?: {
    selectedFile: File | null;
    prevImgURL?: string;
  };
};
export async function updateUser({
  data,
  representativeChar,
  userUID,
  fileList,
}: Props) {
  // 기존이미지는 삭제하고, 신규이미지는 추가

  let imgURL = data?.photoURL || '';
  if (fileList) {
    imgURL = await updateImgAndGetURL({
      selectedFile: fileList?.selectedFile,
      prevImgURL: fileList?.prevImgURL,
      collectionPath: 'user',
    });
  }

  if (data) {
    await usersRef.doc(userUID).set(
      {
        photoURL: imgURL,
        ...data,
        ...representativeChar,
      },
      { merge: true },
    );
    if (user)
      return user.updateProfile({
        displayName: data.displayName || '',
        photoURL: imgURL,
      });
  }

  if (!data || representativeChar) {
    // 대표 캐릭터 정보만 반영
    await usersRef.doc(userUID).set({ ...representativeChar }, { merge: true });
  }
}

type UserWalletProps = {
  uids: string[]; //photocardID
  price: number;
  type?: WalletHistoryType;
  memo?: string; // 아이템명 등
};

// 재화 지급 로직
export async function updateUserWallet({
  uids,
  price,
  type = WalletHistoryType.MASTER,
  memo,
}: UserWalletProps) {
  await Promise.all(
    uids.map(async (uid, i) => {
      const prevUser = await usersRef
        .doc(uid)
        .get()
        .then((res) => res.data());
      const prevPrice = prevUser?.wallet;
      // 유저의 재화 히스토리에 로그 추가
      addWalletHistoryByType({ uid, price, type, memo });

      return usersRef.doc(uid).set(
        {
          wallet: prevPrice ? Number(prevPrice) + Number(price) : Number(price),
        },
        { merge: true },
      );
    }),
  );
}

export function deleteUserImg({ imgURL, userUID }) {
  if (imgURL) deleteImgStorage(imgURL);
  usersRef.doc(userUID).set({ photoURL: '' }, { merge: true });
}

export async function updateUserAuth(userId, auth) {
  return usersRef.doc(userId).set({ authorizationType: auth }, { merge: true });
}

// 유저정보 collection에 저장
export async function setUserProfile({ user, displayName, requestMessage }) {
  const { email, uid, photoURL, emailVerified } = user;

  const userRef = firestore.collection(CollectionPath.USERS).doc(uid);
  const userData: User = {
    uid,
    email,
    displayName: displayName || '',
    photoURL: photoURL || '',
    emailVerified,
    anonymousPassword: '',
    createdAt: new Date(),
    updatedAt: new Date(),
    authorizationType: AUTH_TYPE.GUEST,
    requestMessage: requestMessage,
  };

  return userRef.set(userData, { merge: true });
}

export async function setAnonymousProfile(uid) {
  const userRef = firestore.collection(CollectionPath.USERS).doc(uid);

  return userRef.set(
    {
      uid,
      email: '',
      displayName: '',
      photoURL: '',
      emailVerified: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      anonymousPassword: '',
      authorizationType: AUTH_TYPE.ANONYMOUS,
      requestMessage: '',
    },
    { merge: true },
  );
}
